import React, { useEffect, useMemo, useState } from "react";
import { Nav } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import { Icon } from "@iconify/react";
import { getActiveBranchId, setActiveBranchId } from "../erpClient";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { useHrmsBranches } from "../hooks/useHrmsBranches";
import {
  canAdvancesTab,
  canHolidaysTab,
  canIncentivesTab,
  canOvertimeTab,
  canPayrollTab,
  canProfilesTab,
  canAttendanceTab,
} from "../utils/hrmsTabAccess";
import { HolidaysTab } from "../components/HrmsHolidaysTab";
import { ProfilesTab } from "../components/HrmsProfilesTab";
import { IncentivesTab } from "../components/HrmsIncentivesTab";
import { OvertimeTab } from "../components/HrmsOvertimeTab";
import { AdvancesTab } from "../components/HrmsAdvancesTab";
import { PayrollTab } from "../components/HrmsPayrollTab";
import { AttendanceTab } from "../components/HrmsAttendanceTab";
import "./HrmsPage.css";

const TAB_DEFS = [
  {
    id: "holidays",
    label: "Holidays",
    can: canHolidaysTab,
    icon: "solar:calendar-linear",
  },
  {
    id: "profiles",
    label: "Payroll profiles",
    can: canProfilesTab,
    icon: "solar:user-id-linear",
  },
  {
    id: "incentives",
    label: "Incentives",
    can: canIncentivesTab,
    icon: "solar:wallet-money-linear",
  },
  {
    id: "overtime",
    label: "Overtime",
    can: canOvertimeTab,
    icon: "solar:clock-circle-linear",
  },
  {
    id: "attendance",
    label: "Attendance",
    can: canAttendanceTab,
    icon: "solar:login-2-linear",
  },
  {
    id: "advances",
    label: "Salary advances",
    can: canAdvancesTab,
    icon: "solar:hand-money-linear",
  },
  {
    id: "payroll",
    label: "Payroll",
    can: canPayrollTab,
    icon: "solar:document-text-linear",
  },
];

export function HrmsPage() {
  const { cardClass, buttonTableStyle } = useButtonTableStyle();
  const {
    branchOptions,
    loading: branchesLoading,
    error: branchError,
  } = useHrmsBranches();

  const [branchId, setBranchId] = useState(null);
  const [tab, setTab] = useState("holidays");

  useEffect(() => {
    const b = getActiveBranchId();
    if (b != null) {
      setBranchId((prev) => (prev == null ? b : prev));
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      const b = getActiveBranchId();
      if (b != null) setBranchId(b);
    };
    window.addEventListener("permissionsUpdated", sync);
    return () => window.removeEventListener("permissionsUpdated", sync);
  }, []);

  useEffect(() => {
    if (branchId == null && branchOptions.length === 1) {
      setBranchId(branchOptions[0].value);
    }
  }, [branchId, branchOptions]);

  const onBranchChange = (nextId) => {
    setBranchId(nextId);
    if (nextId != null) setActiveBranchId(nextId);
  };

  const [permTick, setPermTick] = useState(0);

  useEffect(() => {
    const fn = () => setPermTick((n) => n + 1);
    window.addEventListener("permissionsUpdated", fn);
    window.addEventListener("erpAuthUpdated", fn);
    return () => {
      window.removeEventListener("permissionsUpdated", fn);
      window.removeEventListener("erpAuthUpdated", fn);
    };
  }, []);

  useEffect(() => {
    if (branchError) toast.error(branchError);
  }, [branchError]);

  const visibleTabs = useMemo(
    () => TAB_DEFS.filter((t) => t.can()),
    [permTick],
  );

  useEffect(() => {
    if (!visibleTabs.length) return;
    if (!visibleTabs.some((t) => t.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [visibleTabs, tab]);

  const renderTab = () => {
    const props = {
      branchId,
      onBranchChange: onBranchChange,
      branchOptions,
      branchesLoading,
    };
    switch (tab) {
      case "holidays":
        return <HolidaysTab {...props} />;
      case "profiles":
        return <ProfilesTab {...props} />;
      case "incentives":
        return <IncentivesTab {...props} />;
      case "overtime":
        return <OvertimeTab {...props} />;
      case "attendance":
        return <AttendanceTab {...props} />;
      case "advances":
        return <AdvancesTab {...props} />;
      case "payroll":
        return <PayrollTab {...props} />;
      default:
        return null;
    }
  };

  if (!visibleTabs.length) {
    return (
      <div className="body-wrapper">
        <Toaster position="top-center" />
        <div className="container-fluid py-5 text-center text-muted">
          You do not have access to any HRMS features.
        </div>
      </div>
    );
  }

  return (
    <div className="body-wrapper">
      <Toaster position="top-center" />
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden hrms-page-card ${cardClass} ${
            buttonTableStyle === "square" ? "rounded-0" : ""
          }`}
        >
          <div className="hrms-page-header px-4 py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 bg-white text-start">
            <div className="d-flex align-items-center gap-2">
              <h4 className="card-title mb-0">
                Human Resource Management System
              </h4>
            </div>
          </div>
          <div className="card-body p-0 d-flex flex-column">
            <div className="hrms-page-tabs-wrap border-bottom px-4 bg-white">
              <Nav
                variant="tabs"
                className="hrms-module-tabs flex-nowrap overflow-auto justify-content-start"
              >
                {visibleTabs.map((t) => (
                  <Nav.Item key={t.id}>
                    <Nav.Link
                      as="button"
                      type="button"
                      className={`d-flex align-items-center gap-1 text-nowrap small hrms-tab-link ${
                        tab === t.id ? "active" : ""
                      }`}
                      onClick={() => setTab(t.id)}
                    >
                      <Icon icon={t.icon} width={15} height={15} />
                      {t.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
            <div className="hrms-page-body flex-grow-1 p-4 bg-white text-start">
              {renderTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
